import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../services/authAPI";
import moment from "moment";
import "moment/locale/fr";
import Modal from "../components/Modal";
import Field from "../components/Field";
import Select from "../components/Select";

const PRIORITIES_LABELS = {
    LOW: "Faible",
    MEDIUM: "Moyenne",
    HIGH: "Importante",
};

const PRIORITIES_CLASSES = {
    LOW: "primary",
    MEDIUM: "warning",
    HIGH: "danger",
};

export default function VehiclePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vehicle, setVehicle] = useState(null);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editLicensePlateNumber, setEditLicensePlateNumber] = useState("");
    const [editVin, setEditVin] = useState("");
    const [editName, setEditName] = useState("");
    const [editBrand, setEditBrand] = useState("");
    const [editModel, setEditModel] = useState("");
    const [editIsArchived, setEditIsArchived] = useState(false);

    const [editViolations, setEditViolations] = useState([]);

    const [showTicketModal, setShowTicketModal] = useState(false);
    const [priorities, setPriorities] = useState([]);
    const [ticketMileage, setTicketMileage] = useState("");
    const [ticketDescription, setTicketDescription] = useState("");
    const [ticketPriority, setTicketPriority] = useState("");
    const [ticketViolations, setTicketViolations] = useState([]);

    async function fetchVehicle() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicles/${id}`);
            if (!response.ok) {
                setError("Véhicule introuvable.");
                return;
            }
            const data = await response.json();
            setVehicle(data);
        } catch (err) {
            setError("Erreur lors du chargement du véhicule.");
            // silently fail
        } finally {
            setLoading(false);
        }
    }

    async function fetchPriorities() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/ticket_priorities`);
            const data = await response.json();
            setPriorities(data);
        } catch (err) {
            // silently fail
        }
    }

    async function fetchBrands() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicle_brands`);
            const data = await response.json();
            setBrands(data);
        } catch (err) {
            // silently fail
        }
    }

    useEffect(() => {
        fetchVehicle();
        fetchBrands();
        fetchPriorities();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    function openEditModal() {
        setEditLicensePlateNumber(vehicle.licensePlateNumber);
        setEditVin(vehicle.vin ?? "");
        setEditName(vehicle.name ?? "");
        setEditBrand(vehicle.brand ?? "");
        setEditModel(vehicle.model ?? "");
        setEditIsArchived(vehicle.isArchived ?? false);
        setEditViolations([]);
        setShowEditModal(true);
    }

    function openTicketModal() {
        setTicketMileage("");
        setTicketDescription("");
        setTicketPriority("");
        setTicketViolations([]);
        setShowTicketModal(true);
    }

    async function handleCreateTicket() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/tickets`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    priority: ticketPriority,
                    vehicle: '/api/vehicles/' + id,
                    mileage: ticketMileage !== "" ? parseInt(ticketMileage, 10) : null,
                    description: ticketDescription,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setTicketViolations(errorData.violations ?? []);
                return;
            }
            setShowTicketModal(false);
            setTicketViolations([]);
            fetchVehicle();
        } catch (err) {
            // silently fail
        }
    }

    async function handleEditVehicle() {
        try {
            const response = await authFetch(`${process.env.REACT_APP_API_URL}/api/vehicles/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    licensePlateNumber: editLicensePlateNumber,
                    vin: editVin,
                    name: editName,
                    brand: editBrand,
                    model: editModel,
                    isArchived: editIsArchived,
                }),
            });
            if (!response.ok) {
                const errorData = await response.json();
                setEditViolations(errorData.violations ?? []);
                return;
            }
            setShowEditModal(false);
            setEditViolations([]);
            fetchVehicle();
        } catch (err) {
            // silently fail
        }
    }

    const BRAND_LABELS = Object.fromEntries(brands.map((b) => [b.value, b.label]));

    if (loading) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-muted">Chargement...</p>
                    </div>
                </div>
            </section>
        );
    }

    if (error || !vehicle) {
        return (
            <section>
                <div className="card mb-4">
                    <div className="card-body py-5 px-5 text-center">
                        <p className="text-danger">{error ?? "Véhicule introuvable."}</p>
                        <button className="btn btn-link" onClick={() => navigate("/vehicles")}>
                            Retour aux véhicules
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    const STATUS_LABELS = {
        PENDING: "En attente",
        IN_PROGRESS: "En cours",
        SCHEDULED: "Programmé",
        NOT_REQUIRED: "Non nécessaire",
        RESOLVED: "Résolu"
    }

    const STATUS_CLASSES = {
        PENDING: "secondary",
        IN_PROGRESS: "primary",
        SCHEDULED: "info",
        NOT_REQUIRED: "dark",
        RESOLVED: "success"
    }

    return (
        <>
        <Modal
            title="Créer un ticket"
            body={
                <>
                    {ticketViolations.length > 0 && (
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="fas fa-times-circle me-3"></i>
                            <ul className="mb-0 list-unstyled">
                                {ticketViolations.map((v, i) => (
                                    <li key={i}>{v.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <Field
                        name="mileage"
                        label="Kilométrage"
                        value={ticketMileage}
                        onChange={(e) => setTicketMileage(e.target.value)}
                        type="number"
                    />
                    <Select
                        name="priority"
                        label="Priorité"
                        value={ticketPriority}
                        onChange={(e) => setTicketPriority(e.target.value)}
                        options={priorities.map((p) => ({ value: p.value, label: p.label }))}
                    />
                    <Field
                        name="description"
                        label="Description"
                        value={ticketDescription}
                        onChange={(e) => setTicketDescription(e.target.value)}
                        type="text"
                    />
                </>
            }
            button={{ close: "Annuler", valid: "Créer" }}
            showModal={showTicketModal}
            setShowModal={setShowTicketModal}
            onSubmit={handleCreateTicket}
        />
        <Modal
            title="Modifier le véhicule"
            body={
                <>
                    {editViolations.length > 0 && (
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="fas fa-times-circle me-3"></i>
                            <ul className="mb-0 list-unstyled">
                                {editViolations.map((v, i) => (
                                    <li key={i}>{v.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    <Field
                        name="licensePlateNumber"
                        label="Plaque d'immatriculation"
                        value={editLicensePlateNumber}
                        onChange={(e) => setEditLicensePlateNumber(e.target.value)}
                        type="text"
                    />
                    <Field
                        name="vin"
                        label="VIN"
                        value={editVin}
                        onChange={(e) => setEditVin(e.target.value)}
                        type="text"
                    />
                    <Field
                        name="name"
                        label="Nom"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        type="text"
                    />
                    <Select
                        name="brand"
                        label="Marque"
                        value={editBrand}
                        onChange={(e) => setEditBrand(e.target.value)}
                        options={brands.map((b) => ({ value: b.value, label: b.label }))}
                    />
                    <Field
                        name="model"
                        label="Modèle"
                        value={editModel}
                        onChange={(e) => setEditModel(e.target.value)}
                        type="text"
                    />
                    <div className="form-check mt-2">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={!editIsArchived}
                            onChange={(e) => setEditIsArchived(!e.target.checked)}
                            id="flexCheckEditArchived"
                        />
                        <label className="form-check-label" htmlFor="flexCheckEditArchived">Visible</label>
                    </div>
                </>
            }
            button={{ close: "Annuler", valid: "Modifier" }}
            showModal={showEditModal}
            setShowModal={setShowEditModal}
            onSubmit={handleEditVehicle}
        />
        <section>
            <div className="card mb-4">
                <div className="card-header py-3">
                    <div className="row py-3 align-items-center">
                        <div className="col-sm-6 text-center text-sm-start">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() => navigate(-1)}
                            >
                                <i className="fas fa-arrow-left me-2"></i>
                                Retour
                            </button>
                        </div>
                        <div className="col-sm-6 text-center text-sm-end">
                            <button
                                type="button"
                                className="btn btn-secondary me-2"
                                onClick={openTicketModal}
                            >
                                Ticket
                                <i className="fas fa-plus ms-2"></i>
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={openEditModal}
                            >
                                Éditer
                                <i className="fas fa-pen ms-2"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body py-5 px-5">
                    <div className="row g-4 mb-5">
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Plaque d'immatriculation</h6>
                            <p className="fw-bold mb-0">{vehicle.licensePlateNumber}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>VIN</h6>
                            <p className="mb-0">{vehicle.vin || <span className="text-muted">—</span>}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Nom</h6>
                            <p className="mb-0">{vehicle.name || <span className="text-muted">—</span>}</p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Marque / Modèle</h6>
                            <p className="mb-0">
                                {BRAND_LABELS[vehicle.brand] ?? vehicle.brand}
                                {vehicle.model && <span className="text-muted ms-2">{vehicle.model}</span>}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Ajouté le</h6>
                            <p className="mb-0">
                                {moment(vehicle.createdAt).locale("fr").format("dddd Do MMMM YYYY [à] HH:mm")}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <h6 className="text-muted text-uppercase mb-1" style={{ fontSize: "0.75rem" }}>Visible</h6>
                            <p className="mb-0">
                                {vehicle.isArchived ? (
                                    <i className="fa-solid fa-circle-xmark text-danger"></i>
                                ) : (
                                    <i className="fa-solid fa-circle-check text-success"></i>
                                )}
                            </p>
                        </div>
                    </div>

                    <h6 className="fw-bold mb-3">Tickets associés</h6>
                    {vehicle.tickets && vehicle.tickets.length > 0 ? (
                        <table className="table table-hover align-middle mb-0 bg-white">
                            <thead className="bg-light">
                                <tr>
                                    <th className="text-left">Ticket</th>
                                    <th className="text-center">Priorité</th>
                                    <th className="text-center">Statut</th>
                                    <th className="text-center">Kilométrage</th>
                                    <th className="text-center">Visible</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vehicle.tickets.map((ticket) => (
                                    <tr key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)} style={{ cursor: "pointer" }}>
                                        <td>
                                            <p className="fw-bold mb-1">{ticket.ticketNumber} <span className={"badge badge-" + (PRIORITIES_CLASSES[ticket.priority] ?? "secondary")}>
                                                {PRIORITIES_LABELS[ticket.priority] ?? ticket.priority}
                                            </span></p>
                                            <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                                                {moment(ticket.createdAt).locale("fr").format("Do MMMM YYYY [à] HH:mm")}
                                            </p>
                                            {ticket.description && (
                                                <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
                                                    {ticket.description}
                                                </p>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <span className={"badge badge-" + (PRIORITIES_CLASSES[ticket.priority] ?? "secondary")}>
                                                {PRIORITIES_LABELS[ticket.priority] ?? ticket.priority}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className={"badge badge-" + (STATUS_CLASSES[ticket.status])}>
                                                {STATUS_LABELS[ticket.status]}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {ticket.mileage != null ? `${ticket.mileage.toLocaleString("fr-FR")} km` : <span className="text-muted">—</span>}
                                        </td>
                                        <td className="text-center">
                                            {ticket.isVisible ? (
                                                <i className="fa-solid fa-circle-check text-success"></i>
                                            ) : (
                                                <i className="fa-solid fa-circle-xmark text-danger"></i>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p className="text-muted">Aucun ticket associé à ce véhicule.</p>
                    )}
                </div>
            </div>
        </section>
        </>
    );
}
